import { BandMark } from "@/components/terminal/band-mark"
import { Button } from "@/components/ui/button"
import { AssessmentBand } from "@/constants/terminal/assessment-band"
import { cn } from "@/lib/utils"

// One column head for every table, the company list's and every reading
// table's: the micro role in semibold, primary ink: a head is how a reader
// finds their way (the ink rule, Dhanur 26 October 2026). A click sorts by
// the column; the active column turns primary with its label underlined, and
// nothing marks the direction (Dhanur, 26 October 2026). A score column's
// head is the band mark glyph, centred over the row's score square,
// underlined when active.
const SORT_HEAD_TEXT_CLASS = "type-micro text-text-primary font-semibold"

export const SortHeader = ({
  label,
  title,
  isScore = false,
  isActive,
  isDescending,
  onSort,
  className,
}: {
  label: string
  // The accessible name ("Sort by company"); the label when absent.
  title?: string
  isScore?: boolean
  isActive: boolean
  isDescending: boolean
  onSort: () => void
  className?: string
}) => {
  const name = title ?? label
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      onClick={onSort}
      aria-pressed={isActive}
      aria-label={
        isActive
          ? `${name}, ${isDescending ? "descending" : "ascending"}`
          : name
      }
      data-measure=""
      className={cn(
        SORT_HEAD_TEXT_CLASS,
        "hover:text-foreground relative inline-flex h-4 max-w-full items-center justify-start px-0 transition-colors hover:no-underline",
        "focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none focus-visible:ring-inset",
        isActive && "text-foreground",
        isScore && "w-(--terminal-score-square-size-row) justify-center",
        className
      )}
    >
      {isScore ? (
        <>
          <BandMark band={AssessmentBand.UNCLEAR} className="opacity-70" />
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute right-1 bottom-[-3px] left-1 h-px bg-current"
            />
          )}
        </>
      ) : (
        <span
          className={cn(
            "min-w-0 truncate border-b border-transparent pb-0.5",
            isActive && "border-current"
          )}
        >
          {label}
        </span>
      )}
    </Button>
  )
}

// A head that does not sort: the same box and label as SortHeader, so every
// head in a row shares one baseline.
export const StaticHeader = ({ label }: { label: string }) => (
  <span
    data-measure=""
    className={cn(
      SORT_HEAD_TEXT_CLASS,
      "inline-flex h-4 max-w-full items-center"
    )}
  >
    <span className="min-w-0 truncate border-b border-transparent pb-0.5">
      {label}
    </span>
  </span>
)
